import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { AsyncPipe, DecimalPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnChanges,
  OnInit,
  SimpleChanges,
  contentChild,
  forwardRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map } from 'rxjs';

import { getRuntimeUniqueId } from '@shared/utils/runtime-unique-id';

import { appChevronDown16Icon } from '@icons/chevron-down-16';
import { appSearch20Icon } from '@icons/search-20';

import { ButtonComponent } from '../button';
import { CheckboxComponent } from '../checkbox';
import { LoadingSkeletonComponent } from '../loading-skeleton';
import { TextInputComponent } from '../text-input/text-input.component';
import { MultiSelectCustomOptionDirective } from './directives/multi-select-custom-option.directive';
import { MultiSelectCustomTriggerDirective } from './directives/multi-select-custom-trigger.directive';
import { ALL_OPTION, MultiSelectOptionViewModel, MultiSelectOptionWithSelectedViewModel } from './multi-select.view-model';

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
    },
  ],
  imports: [
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
    TranslatePipe,
    SvgIconComponent,
    OverlayModule,
    TextInputComponent,
    FormsModule,
    CheckboxComponent,
    ButtonComponent,
    DecimalPipe,
    LoadingSkeletonComponent,
  ],
})
export class MultiSelectComponent<T> implements OnInit, OnChanges, ControlValueAccessor {
  readonly #destroyRef = inject(DestroyRef);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #translateService = inject(TranslateService);

  public type = input.required<'multi' | 'single'>();
  public options = input<MultiSelectOptionViewModel<T>[] | null | undefined>();
  public label = input<string>('');
  public allSelectedLabel = input<string | undefined | null>();
  public allSelectedLabelTranslationKey = input<string | undefined | null>();
  public selectedLabel = input<string | undefined | null>();
  public placeholder = input<string>('');
  public withConfirm = input<boolean>(false);
  public withSearch = input<boolean>(false);
  public withSelectAll = input<boolean>(false);
  public optional = input<boolean>(false);
  public optionalOptionLabel = input<string | null | undefined>();
  public selectClasses = input<string>('');
  public dropdownClasses = input<string>('');
  public optionsContainerClasses = input<string>('');
  public size = input<'medium' | 'large'>('medium');
  public error = input<string>();
  public errorInFlow = input(false);
  public isRequired = input<boolean>(false);
  public isLoading = input<boolean>(false);
  public customPositions = input<ConnectedPosition[]>();
  public inline = input(false);

  public readonly dismissed = output<void>();

  public trigger = viewChild<string, ElementRef<HTMLElement>>('trigger', {
    read: ElementRef,
  });

  public readonly customTriggerDirective = contentChild(MultiSelectCustomTriggerDirective);
  public readonly customOptionDirective = contentChild(MultiSelectCustomOptionDirective);

  public readonly selectUuid = `select-checkbox-${getRuntimeUniqueId()}`;

  public isDisabled = false;
  public isDropdownOpen = false;
  public isSelectAll = false;
  public isSelectAllIndeterminate = false;

  private revertValue: T | T[] | null = null;

  private readonly value$ = new BehaviorSubject<T | T[] | null>(null);
  private readonly options$ = new BehaviorSubject<MultiSelectOptionViewModel<T>[] | null | undefined>(this.options());
  private readonly search$ = new BehaviorSubject<string>('');

  private readonly optionsWithSelections$ = combineLatest([this.value$, this.options$]).pipe(
    map(([value, options]) =>
      (options || []).map((option): MultiSelectOptionWithSelectedViewModel<T> => {
        const optionIsSelected = (Array.isArray(value) && value.includes(option.value)) || value === option.value;
        return { ...option, selected: optionIsSelected };
      })
    ),
    takeUntilDestroyed(this.#destroyRef)
  );
  public readonly filteredOptions$ = combineLatest([this.optionsWithSelections$, this.search$]).pipe(
    map(([options, search]) => this.filterOptions(options, search)),
    takeUntilDestroyed(this.#destroyRef)
  );
  public readonly selections$ = this.optionsWithSelections$.pipe(
    map((options) => options.filter(({ selected }) => selected)),
    takeUntilDestroyed(this.#destroyRef)
  );
  public readonly optionsCount$ = this.optionsWithSelections$.pipe(
    map((options) => options.length),
    distinctUntilChanged(),
    takeUntilDestroyed(this.#destroyRef)
  );

  public readonly positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: -4,
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: -4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: 4,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: 4,
    },
  ];

  private onChange: (value: T[] | T | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    injectRegisterIcons([appChevronDown16Icon, appSearch20Icon]);
  }

  public ngOnInit(): void {
    this.watchSelectAllChanges();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['withSelectAll'] || changes['optional'] || changes['optionalOptionLabel'] || changes['type']) {
      if (this.type() === 'single' && this.withSelectAll()) {
        this.options$.next(this.mapOptionsWithAllOption(this.options()));
      } else if (this.type() === 'single' && this.optional()) {
        this.options$.next(this.mapOptionsWithNullOption(this.options()));
      } else {
        this.options$.next(this.options());
      }
    }
  }

  public get isOpen(): boolean {
    return this.inline() || this.isDropdownOpen;
  }

  public get search(): string {
    return this.search$.getValue();
  }

  public set search(search: string) {
    this.search$.next(search);
  }

  public get value(): T | T[] | null {
    return this.value$.getValue();
  }

  public set value(val: T | T[] | null) {
    this.value$.next(val);
  }

  public get isConfirm(): boolean {
    return this.withConfirm() || this.type() === 'multi';
  }

  public writeValue(value: T[] | T | null): void {
    if (this.type() === 'multi') {
      if (value === null || Array.isArray(value)) {
        this.value = value;
        this.revertValue = value;
      }
    } else {
      this.value = value;
      this.revertValue = value;
    }
    this.#cdr.markForCheck();
  }

  public registerOnChange(onChange: (value: T[] | T | null) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled && this.isDropdownOpen) {
      this.isDropdownOpen = false;
    }
    this.isDisabled = isDisabled;
    this.#cdr.markForCheck();
  }

  public onTriggerClicked(): void {
    if (this.isDisabled || this.isLoading()) return;

    if (this.isDropdownOpen) {
      this.onCancelClick();
      return;
    }

    if (this.isConfirm) {
      this.revertValue = Array.isArray(this.value) ? [...this.value] : this.value;
    }
    this.openDropdown();
  }

  public onOptionClick(optionValue: T, isDisabled = false): void {
    if (this.isDisabled || isDisabled) return;

    if (this.type() === 'multi') {
      this.handleMultiOptionClick(optionValue);
    } else {
      this.handleSingleOptionClick(optionValue);
    }

    if (!this.isConfirm) {
      this.closeMoreMenu();
      this.emit(this.value);
    }
  }

  public onMultiSelectAllChange(isSelectAll: boolean, filteredOptions: MultiSelectOptionViewModel<T>[] | undefined): void {
    if (this.isDisabled) {
      this.isSelectAll = !isSelectAll;
      return;
    }

    const value = (this.value as T[] | null) ?? [];
    const filteredValues = filteredOptions?.filter(({ disabled }) => !disabled).map(({ value }) => value) ?? [];

    let newValue: T[] | null = isSelectAll
      ? [...new Set([...(value || []), ...filteredValues])]
      : (value || []).filter((v) => !filteredValues.includes(v));

    if (newValue.length === 0) {
      newValue = null;
    }
    this.value = newValue;

    if (!this.isConfirm) {
      this.closeMoreMenu();
      this.emit(this.value);
    }
  }

  public onCancelClick(): void {
    if (this.isConfirm) {
      this.value = this.revertValue;
    }
    this.closeMoreMenu();
  }

  public onConfirmClick(): void {
    this.emit(this.value);
    this.closeMoreMenu();
  }

  public onOutsideClick(event: MouseEvent): void {
    const triggerElement = this.trigger()?.nativeElement;

    if (!triggerElement || event.target === triggerElement || triggerElement.contains(event.target as Node)) {
      return;
    }

    this.onCancelClick();
  }

  public onOverlayKeydown(event?: KeyboardEvent): void {
    if (event && event.key !== 'Escape') return;

    this.closeMoreMenu();
  }

  private openDropdown(): void {
    if (this.isDisabled || this.isLoading()) return;

    this.isDropdownOpen = true;
  }

  private closeMoreMenu(): void {
    this.isDropdownOpen = false;
    this.search$.next('');
    this.onTouched?.();
    this.dismissed.emit();
    this.#cdr.markForCheck();
  }

  private emit(value: T | T[] | null): void {
    this.onChange(value);
    this.onTouched();
  }

  private handleMultiOptionClick(optionValue: T): void {
    let newValue: T[] | null = null;

    if (Array.isArray(this.value)) {
      newValue = this.value.includes(optionValue) ? this.value.filter((v) => v !== optionValue) : [...this.value, optionValue];
      if (newValue.length === 0) {
        newValue = null;
      }
    } else {
      newValue = optionValue === null ? null : [optionValue];
    }

    this.value = newValue;
  }

  private handleSingleOptionClick(optionValue: T): void {
    let newValue: T | null = null;

    if (!Array.isArray(this.value)) {
      newValue = optionValue;
    }

    this.value = newValue;
  }

  private filterOptions(
    options: MultiSelectOptionWithSelectedViewModel<T>[] | undefined,
    search: string
  ): MultiSelectOptionWithSelectedViewModel<T>[] | undefined {
    if (!search) return [...(options || [])];
    return options?.filter(
      (option) =>
        option.alwaysShownInSearchResult || (option.searchLabel ?? option.label).trim().toLowerCase().includes(search.trim().toLowerCase())
    );
  }

  private watchSelectAllChanges(): void {
    combineLatest([this.filteredOptions$, this.selections$])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([filteredOptions, selections]) => {
        const selectableOptions = filteredOptions?.filter(({ disabled }) => !disabled) ?? [];
        this.isSelectAll = !!(selections?.length && selectableOptions.length > 0 && selectableOptions.every(({ selected }) => selected));
        this.isSelectAllIndeterminate = !!(
          selections?.length &&
          selectableOptions.length > 0 &&
          !selectableOptions.every(({ selected }) => selected) &&
          selectableOptions.some(({ selected }) => selected)
        );
        this.#cdr.markForCheck();
      });
  }

  private mapOptionsWithAllOption(
    options: MultiSelectOptionViewModel<T>[] | null | undefined
  ): MultiSelectOptionViewModel<T>[] | null | undefined {
    if (!options) return;

    const allOption: MultiSelectOptionViewModel<T> = {
      value: ALL_OPTION as T,
      label: this.#translateService.instant('shared.multi-select.all-option'),
    };

    return options.length > 0 ? [allOption, ...options] : [];
  }

  private mapOptionsWithNullOption(
    options: MultiSelectOptionViewModel<T>[] | null | undefined
  ): MultiSelectOptionViewModel<T>[] | null | undefined {
    if (!options) return;

    const nullOption: MultiSelectOptionViewModel<T> = {
      value: null as T,
      label: this.optionalOptionLabel() ?? this.#translateService.instant('shared.multi-select.none-option'),
    };

    return options.length > 0 ? [nullOption, ...options] : [];
  }
}
